import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import {
  FaAddressBook,
  FaEye,
  FaEyeSlash,
  FaFolderPlus,
  FaImage,
  FaInfoCircle,
  FaLink,
  FaPlus,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import "react-quill/dist/quill.snow.css";

import AdminDrawer from "./AdminDrawer";

import {
  buildSectionPayload,
  emptySectionForm,
  mapSectionToForm,
  normalizeText,
  resolveMediaUrl,
  slugify,
} from "../utils/sectionAdminUtils";

const emptyCategoryForm = {
  label: "",
  icon: "",
  ordre: 999,
};

export default function SectionFormDrawer({
  isOpen,
  mode = "create",
  section,
  categories = [],
  saving,
  categorySaving = false,
  onClose,
  onCreate,
  onUpdate,
  onCreateCategory,
  onDeleteCategory,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptySectionForm);
  const [showCategoryCreator, setShowCategoryCreator] = useState(false);
  const [newCategory, setNewCategory] = useState(emptyCategoryForm);

  const selectedCategoryObject = categories.find(
  (category) => String(category.id) === String(formData.category_id)
);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && section) {
      setFormData(mapSectionToForm(section));
    } else {
      setFormData(emptySectionForm);
    }

    setShowCategoryCreator(false);
    setNewCategory(emptyCategoryForm);
  }, [isOpen, isEdit, section]);

  const title = isEdit ? "Modifier une association" : "Ajouter une association";

  const subtitle = isEdit
    ? "Mettez à jour les informations publiques, les visuels et les coordonnées."
    : "Créez une nouvelle fiche association pour le portail public.";

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNameChange = (event) => {
    const value = event.target.value;
    const generatedPath = value ? `/associations/${slugify(value)}` : "";

    setFormData((prev) => ({
      ...prev,
      nom_section: value,
      slug: prev.slug || generatedPath,
      lien_section: prev.lien_section || generatedPath,
    }));
  };

  const handleNewCategoryChange = (event) => {
    const { name, value } = event.target;

    setNewCategory((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateCategoryInline = async () => {
    const label = String(newCategory.label || "").trim();

    if (!label) {
      toast.error("Le nom de la catégorie est obligatoire.");
      return;
    }

    const existingCategory = categories.find(
      (category) => normalizeText(category.label) === normalizeText(label)
    );

    if (existingCategory) {
      setFormData((prev) => ({
        ...prev,
        category_id: existingCategory.id,
      }));

      setShowCategoryCreator(false);
      setNewCategory(emptyCategoryForm);

      toast.info("Cette catégorie existe déjà : elle a été sélectionnée.");
      return;
    }

    const created = await onCreateCategory?.({
      label,
      icon: newCategory.icon || null,
      ordre: newCategory.ordre || 999,
      is_active: true,
    });

    if (!created?.id) return;

    setFormData((prev) => ({
      ...prev,
      category_id: created.id,
    }));

    setShowCategoryCreator(false);
    setNewCategory(emptyCategoryForm);
  };

  const handleDeleteSelectedCategory = async () => {
  if (!selectedCategoryObject) {
    toast.info("Sélectionne d’abord une catégorie à supprimer.");
    return;
  }

  const deleted = await onDeleteCategory?.(selectedCategoryObject);

  if (!deleted) return;

  setFormData((prev) => ({
    ...prev,
    category_id: "",
  }));
};

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.nom_section) {
      toast.error("Le nom de l'association est obligatoire.");
      return;
    }

    const payload = buildSectionPayload(formData);

    if (isEdit) {
      await onUpdate?.(section.id, payload);
    } else {
      await onCreate?.(payload);
    }
  };

  const coverPreview = resolveMediaUrl(
    formData.photoURL,
    "/images/asso_placeholder.jpg"
  );

  const logoPreview = resolveMediaUrl(
    formData.logo_section,
    "/images/asso_placeholder.jpg"
  );

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-6xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <FormBlock
            icon={<FaInfoCircle />}
            title="Informations générales"
            subtitle="Nom, catégorie, ordre d’affichage et visibilité."
          >
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="sm:col-span-2">
                <InputField
                  label="Nom de l’association"
                  name="nom_section"
                  value={formData.nom_section}
                  onChange={handleNameChange}
                  required
                />
              </div>

              <InputField
                label="Discipline / activité"
                name="discipline_section"
                value={formData.discipline_section}
                onChange={handleChange}
              />

              <div>
  <div className="mb-1 flex items-center justify-between gap-2">
    <label className="block text-sm font-bold text-slate-700 dark:text-slate-200">
      Catégorie
    </label>

    <div className="flex items-center gap-3">
      {selectedCategoryObject && (
        <button
          type="button"
          onClick={handleDeleteSelectedCategory}
          disabled={categorySaving || saving}
          className="inline-flex items-center gap-1 text-xs font-bold text-rose-600 transition hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-rose-400"
        >
          <FaTimes />
          Supprimer
        </button>
      )}

      <button
        type="button"
        onClick={() => setShowCategoryCreator((current) => !current)}
        className="inline-flex items-center gap-1 text-xs font-bold text-primary transition hover:opacity-80"
      >
        {showCategoryCreator ? (
          <>
            <FaTimes />
            Fermer
          </>
        ) : (
          <>
            <FaPlus />
            Créer
          </>
        )}
      </button>
    </div>
  </div>

  <select
    name="category_id"
    value={formData.category_id}
    onChange={handleChange}
    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
  >
    <option value="">Non catégorisée</option>
    {categories.map((category) => (
      <option key={category.id} value={category.id}>
        {category.icon ? `${category.icon} ` : ""}
        {category.label}
      </option>
    ))}
  </select>

  {selectedCategoryObject && (
    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
      Catégorie sélectionnée :{" "}
      <span className="font-bold">{selectedCategoryObject.label}</span>
    </p>
  )}
</div>

              {showCategoryCreator && (
                <div className="sm:col-span-2 xl:col-span-4">
                  <div className="rounded-[22px] border border-primary/20 bg-primary/5 p-4 dark:border-primary/30 dark:bg-primary/10">
                    <div className="mb-4 flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-primary shadow-sm dark:bg-slate-950">
                        <FaFolderPlus />
                      </span>

                      <div>
                        <p className="font-bold text-slate-950 dark:text-white">
                          Nouvelle catégorie
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          Elle sera créée dans la table des catégories puis
                          automatiquement associée à cette fiche.
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-[1fr_140px_140px_auto]">
                      <InputField
                        label="Nom"
                        name="label"
                        value={newCategory.label}
                        onChange={handleNewCategoryChange}
                      />

                      <InputField
                        label="Icône"
                        name="icon"
                        value={newCategory.icon}
                        onChange={handleNewCategoryChange}
                      />

                      <InputField
                        label="Ordre"
                        name="ordre"
                        type="number"
                        value={newCategory.ordre}
                        onChange={handleNewCategoryChange}
                      />

                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={handleCreateCategoryInline}
                          disabled={categorySaving}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaSave />
                          {categorySaving ? "Création..." : "Créer"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <InputField
                label="Ordre"
                name="ordre"
                type="number"
                value={formData.ordre}
                onChange={handleChange}
              />

              <InputField
                label="ID galerie carrousel"
                name="carrousel_id"
                type="number"
                value={formData.carrousel_id}
                onChange={handleChange}
              />

              <ToggleField
                icon={<FaEye />}
                label="Association active"
                description="Visible sur le portail public."
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />

              <ToggleField
                icon={<FaEyeSlash />}
                label="Mise en avant"
                description="Permet d’identifier une association prioritaire."
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
              />
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaAddressBook />}
            title="Coordonnées"
            subtitle="Responsable, adresse, téléphone, email et réseaux sociaux."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Responsable"
                name="responsable_section"
                value={formData.responsable_section}
                onChange={handleChange}
              />

              <InputField
                label="Téléphone"
                name="tel_section"
                value={formData.tel_section}
                onChange={handleChange}
              />

              <InputField
                label="Email"
                name="mail_section"
                type="email"
                value={formData.mail_section}
                onChange={handleChange}
              />

              <InputField
                label="Site web"
                name="siteweb_section"
                value={formData.siteweb_section}
                onChange={handleChange}
              />

              <div className="sm:col-span-2">
                <InputField
                  label="Adresse"
                  name="adresse_section"
                  value={formData.adresse_section}
                  onChange={handleChange}
                />
              </div>

              <InputField
                label="Facebook"
                name="facebook_section"
                value={formData.facebook_section}
                onChange={handleChange}
              />

              <InputField
                label="Instagram"
                name="instagram_section"
                value={formData.instagram_section}
                onChange={handleChange}
              />
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaImage />}
            title="Visuels"
            subtitle="Logo, photo de vignette et images d’en-tête."
          >
            <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
              <div className="grid gap-4 sm:grid-cols-2">
                <InputField
                  label="Logo"
                  name="logo_section"
                  value={formData.logo_section}
                  onChange={handleChange}
                />

                <InputField
                  label="Photo vignette"
                  name="photoURL"
                  value={formData.photoURL}
                  onChange={handleChange}
                />

                <InputField
                  label="Image header 1"
                  name="header1_URL"
                  value={formData.header1_URL}
                  onChange={handleChange}
                />

                <InputField
                  label="Image header 2"
                  name="header2_URL"
                  value={formData.header2_URL}
                  onChange={handleChange}
                />
              </div>

              <div className="grid gap-3">
                <PreviewCard title="Logo" image={logoPreview} contain />
                <PreviewCard title="Vignette" image={coverPreview} />
              </div>
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaLink />}
            title="Liens internes"
            subtitle="Adresse de la fiche publique et slug éventuel."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
              />

              <InputField
                label="Lien de la section"
                name="lien_section"
                value={formData.lien_section}
                onChange={handleChange}
              />
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaInfoCircle />}
            title="À propos"
            subtitle="Texte de présentation affiché sur la fiche publique."
          >
            <div className="section-admin-quill overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
              <ReactQuill
                value={formData.about_content}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    about_content: value,
                  }))
                }
                theme="snow"
              />
            </div>
          </FormBlock>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || categorySaving}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving || categorySaving}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function FormBlock({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">{title}</h3>
          {subtitle && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}

function ToggleField({ icon, label, description, name, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        {icon}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-slate-900 dark:text-white">
          {label}
        </span>

        <span className="block text-xs text-slate-500 dark:text-slate-400">
          {description}
        </span>
      </span>

      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-5 w-5 rounded border-slate-300 text-primary focus:ring-primary"
      />
    </label>
  );
}

function PreviewCard({ title, image, contain = false }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
      <p className="mb-3 text-sm font-bold text-slate-950 dark:text-white">
        {title}
      </p>

      <div className="aspect-video overflow-hidden rounded-[20px] bg-slate-100 dark:bg-slate-900">
        <img
          src={image}
          alt={title}
          className={[
            "h-full w-full",
            contain ? "object-contain p-3" : "object-cover",
          ].join(" ")}
          onError={(event) => {
            event.currentTarget.src = "/images/asso_placeholder.jpg";
          }}
        />
      </div>
    </div>
  );
}